/**
 * Product Stories rail + shared Quick Shop dialog.
 *
 * Two independent custom elements:
 * - <product-stories-rail>: lazy-loads each story's video only once it's
 *   near the viewport (poster/image shown until then), pauses it when it
 *   leaves, and wires each trigger (only the ones with a real product —
 *   see snippets/product-story-card.liquid, which never renders a
 *   trigger at all without one) to open the shared Quick Shop at that
 *   story's index. V2 adds, per rail instance:
 *   - sound control: every video starts muted (autoplay-compatible); a
 *     per-card toggle makes that one video audible. The "wants sound"
 *     preference is shared by the whole instance (never persisted), only
 *     one video is ever audible, and the audible slot follows the most
 *     visible story when the current one scrolls away.
 *   - Quick Add under each card: single-variant products add directly,
 *     multi-variant products get a compact option overlay. Both POST to
 *     /cart/add.js with quantity 1 and then dispatch the same
 *     `pdp:added-to-cart` event the PDP / Quick Shop use, which is what
 *     opens the existing Cart Drawer. There is no confirm step: the moment
 *     the picks resolve exactly one in-stock variant it is added.
 *   Every listener is registered through one AbortController and the
 *   observer / timers are torn down in disconnectedCallback, so a Theme
 *   Editor section reload (old element removed, new one connected) never
 *   leaves duplicates behind.
 * - <product-quick-shop>: one shared native <dialog> per section (not
 *   one per card) that fetches each product's real data from Shopify's
 *   own /products/{handle}.js endpoint the first time it's shown — never
 *   embedded in the page up front — and renders a real variant picker,
 *   live price/availability/media updates, and a real add-to-cart
 *   against /cart/add.js. Modeled on the mobile drawer's native <dialog>
 *   pattern (assets/header.js) for focus trap / Escape / focus return,
 *   but not sharing code with it — this needs async content swapping
 *   between stories, which that component doesn't.
 */

(function initProductStoriesRail() {
  if (customElements.get('product-stories-rail')) return;

  const AUDIO_CLAIM_EVENT = 'product-stories:audio-claim';
  const SOUND_VISIBLE_RATIO = 0.4;
  const SCROLL_SETTLE_MS = 140;
  const ADDED_FEEDBACK_MS = 1400;

  function formatTemplate(template, values) {
    return Object.keys(values).reduce((text, key) => text.split(`__${key}__`).join(values[key]), template || '');
  }

  customElements.define(
    'product-stories-rail',
    class ProductStoriesRail extends HTMLElement {
      connectedCallback() {
        // A moved element reconnects without a disconnect in between in
        // some browsers' edge cases; always start from a clean slate.
        this.teardown();

        this.abort = new AbortController();
        const { signal } = this.abort;

        this.track = this.querySelector('[data-stories-track]');
        this.prevButton = this.querySelector('[data-stories-prev]');
        this.nextButton = this.querySelector('[data-stories-next]');
        this.triggers = Array.from(this.querySelectorAll('[data-story-trigger]'));
        this.videos = Array.from(this.querySelectorAll('[data-story-video]'));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Audio state: wantsSound is the shared, in-memory preference;
        // audible is the single video currently allowed to make noise.
        this.wantsSound = false;
        this.audible = null;
        this.suspended = false;
        this.settleTimer = null;
        this.feedbackTimers = new Set();
        this.panelState = new WeakMap();

        this.prevButton?.addEventListener('click', () => this.scrollByCard(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.scrollByCard(1), { signal });
        this.track?.addEventListener(
          'scroll',
          () => {
            this.updateArrows();
            this.scheduleReconcile();
          },
          { passive: true, signal }
        );
        window.addEventListener(
          'resize',
          () => {
            this.updateArrows();
            this.scheduleReconcile();
          },
          { signal }
        );
        this.updateArrows();

        this.triggers.forEach((trigger) => {
          const open = () => {
            const quickShop = document.querySelector('product-quick-shop');
            // The Quick Shop modal plays its own media; keep the rail quiet
            // underneath it (resumed when the dialog closes).
            this.suspendAudio();
            quickShop?.open(Number(trigger.dataset.storyIndex), trigger);
          };
          trigger.addEventListener('click', open, { signal });
          // role="button" div, not a native <button> — see the content-model
          // note in snippets/product-story-card.liquid — so Enter/Space
          // activation has to be wired by hand.
          trigger.addEventListener(
            'keydown',
            (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              open();
            },
            { signal }
          );
        });

        this.addEventListener('click', (event) => this.onClick(event), { signal });
        // Document-level: focus may be nowhere (a disabled button just lost it).
        document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

        // Click outside an open option overlay dismisses it.
        document.addEventListener(
          'click',
          (event) => {
            const open = this.querySelector('[data-story-panel]:not([hidden])');
            if (!open) return;
            const buy = open.closest('[data-story-buy]');
            if (buy && !buy.contains(event.target)) this.closePanel(buy, false);
          },
          { signal }
        );

        // Another Product Stories instance went audible: yield.
        document.addEventListener(
          AUDIO_CLAIM_EVENT,
          (event) => {
            if (event.detail?.source === this) return;
            this.wantsSound = false;
            this.setAudible(null);
          },
          { signal }
        );

        // A modal (Quick Shop, Cart Drawer…) closed: bring the audio back
        // if nothing else is still open. `close` doesn't bubble, hence capture.
        document.addEventListener(
          'close',
          (event) => {
            if (!(event.target instanceof HTMLDialogElement)) return;
            if (document.querySelector('dialog[open]')) return;
            this.resumeAudio();
          },
          { capture: true, signal }
        );

        document.addEventListener(
          'visibilitychange',
          () => {
            if (document.hidden) {
              this.setAudible(null);
            } else {
              this.scheduleReconcile();
            }
          },
          { signal }
        );

        this.setupVideoLazyLoad();
      }

      disconnectedCallback() {
        this.teardown();
      }

      teardown() {
        this.abort?.abort();
        this.abort = null;
        this.observer?.disconnect();
        this.observer = null;
        clearTimeout(this.settleTimer);
        this.feedbackTimers?.forEach((timer) => clearTimeout(timer));
        this.feedbackTimers?.clear();
        (this.videos || []).forEach((video) => {
          video.muted = true;
        });
      }

      scrollByCard(direction) {
        const items = this.track.querySelectorAll(':scope > *');
        if (items.length === 0) return;
        const amount =
          items.length > 1
            ? (items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left) * direction
            : items[0].getBoundingClientRect().width * direction;
        this.track.scrollBy({ left: amount, behavior: 'smooth' });
      }

      updateArrows() {
        if (!this.track || !this.prevButton || !this.nextButton) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.prevButton.toggleAttribute('disabled', this.track.scrollLeft <= 4);
        this.nextButton.toggleAttribute('disabled', this.track.scrollLeft >= maxScroll - 4 || maxScroll <= 0);
      }

      setupVideoLazyLoad() {
        if (this.videos.length === 0) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                if (!video.src && video.dataset.src) video.src = video.dataset.src;
                if (!this.reducedMotion) video.play().catch(() => {});
              } else {
                video.pause();
                if (video === this.audible) this.setAudible(null);
              }
            });
            this.scheduleReconcile();
          },
          { rootMargin: '200px 0px', threshold: 0.4 }
        );
        this.videos.forEach((video) => this.observer.observe(video));
      }

      // ---- Event delegation (sound + Quick Add) ----------------------

      onClick(event) {
        const sound = event.target.closest('[data-story-sound]');
        if (sound) {
          this.onSoundClick(sound);
          return;
        }

        const add = event.target.closest('[data-story-add]');
        if (add) {
          const buy = add.closest('[data-story-buy]');
          this.addVariant(buy, add.dataset.variantId, add, () => this.restoreCta(buy));
          return;
        }

        const toggle = event.target.closest('[data-story-toggle]');
        if (toggle) {
          this.openPanel(toggle.closest('[data-story-buy]'));
          return;
        }

        const close = event.target.closest('[data-story-close]');
        if (close) {
          this.closePanel(close.closest('[data-story-buy]'), true);
          return;
        }

        const option = event.target.closest('[data-story-option]');
        if (option && !option.disabled) {
          this.onOptionClick(option);
          return;
        }

      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        if (document.querySelector('dialog[open]')) return;
        const panel = this.querySelector('[data-story-panel]:not([hidden])');
        if (!panel) return;
        this.closePanel(panel.closest('[data-story-buy]'), true);
      }

      // ---- Sound -------------------------------------------------------

      onSoundClick(button) {
        const video = button.closest('[data-story]')?.querySelector('[data-story-video]');
        if (!video) return;

        if (video === this.audible) {
          // Audible -> mute. Muting is an explicit "no sound": the shared
          // preference goes off too, so the next story stays silent.
          this.wantsSound = false;
          this.setAudible(null);
          return;
        }

        this.wantsSound = true;
        this.suspended = false;
        this.makeAudible(video, button);
      }

      async makeAudible(video, button) {
        // Claim before playing so any other instance mutes immediately.
        document.dispatchEvent(new CustomEvent(AUDIO_CLAIM_EVENT, { detail: { source: this } }));

        if (!video.src && video.dataset.src) video.src = video.dataset.src;
        this.videos.forEach((other) => {
          if (other !== video) other.muted = true;
        });
        video.muted = false;

        // Optimistic: the toggle reflects the new owner immediately, so a
        // second tap while play() is still pending mutes it instead of
        // being read as another unmute. The token drops stale results.
        this.audible = video;
        this.audioToken = (this.audioToken || 0) + 1;
        const token = this.audioToken;
        this.syncSoundButtons();

        try {
          await video.play();
        } catch (error) {
          if (token !== this.audioToken) return;
          // Browser refused unmuted playback (autoplay policy, or the
          // media itself can't play). Fall back to muted playback, drop
          // the preference, and tell assistive tech instead of failing
          // silently. The next tap is a fresh user gesture and may work.
          video.muted = true;
          video.play().catch(() => {});
          this.wantsSound = false;
          this.audible = null;
          this.syncSoundButtons();
          this.announce(button, button?.closest('[data-story]')?.querySelector('[data-story-status]'), 'blocked');
          return;
        }

        // Muted (or handed over) while play() was pending: stay quiet.
        if (token !== this.audioToken || this.audible !== video) video.muted = true;
      }

      setAudible(video) {
        this.audioToken = (this.audioToken || 0) + 1;
        this.audible = video;
        this.videos.forEach((other) => {
          other.muted = other !== video;
        });
        this.syncSoundButtons();
      }

      syncSoundButtons() {
        this.querySelectorAll('[data-story-sound]').forEach((button) => {
          const video = button.closest('[data-story]')?.querySelector('[data-story-video]');
          const audible = !!video && video === this.audible && !video.muted;
          button.dataset.muted = audible ? 'false' : 'true';
          button.setAttribute('aria-pressed', audible ? 'true' : 'false');
          button.setAttribute('aria-label', audible ? button.dataset.labelOff : button.dataset.labelOn);
        });
      }

      announce(button, statusEl, kind) {
        if (!statusEl || kind !== 'blocked') return;
        const message = this.dataset.soundBlocked;
        if (!message) return;
        statusEl.textContent = '';
        // Re-set on next frame so repeated identical messages are re-announced.
        requestAnimationFrame(() => {
          statusEl.textContent = message;
        });
      }

      // How much of a video is actually on screen: inside the rail's own
      // scrollport and the window, both axes.
      visibleRatio(video) {
        const box = video.getBoundingClientRect();
        if (box.width === 0 || box.height === 0) return 0;
        const rail = (this.track || this).getBoundingClientRect();
        const left = Math.max(box.left, rail.left, 0);
        const right = Math.min(box.right, rail.right, window.innerWidth);
        const top = Math.max(box.top, rail.top, 0);
        const bottom = Math.min(box.bottom, rail.bottom, window.innerHeight);
        const area = Math.max(0, right - left) * Math.max(0, bottom - top);
        return area / (box.width * box.height);
      }

      scheduleReconcile() {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.reconcileAudio(), SCROLL_SETTLE_MS);
      }

      // With sound wanted, keep exactly one visible video audible: stay on
      // the current one while it is still on screen, otherwise hand the
      // slot to the most visible playing story. Without it, everything
      // stays muted.
      reconcileAudio() {
        if (this.suspended || document.hidden) return;
        if (!this.wantsSound) {
          if (this.audible) this.setAudible(null);
          return;
        }
        if (this.audible && this.visibleRatio(this.audible) >= SOUND_VISIBLE_RATIO) return;

        let best = null;
        let bestRatio = SOUND_VISIBLE_RATIO;
        this.videos.forEach((video) => {
          if (video.paused) return;
          const ratio = this.visibleRatio(video);
          if (ratio > bestRatio) {
            best = video;
            bestRatio = ratio;
          }
        });

        if (best) {
          const button = best.closest('[data-story]')?.querySelector('[data-story-sound]');
          this.makeAudible(best, button);
        } else if (this.audible) {
          this.setAudible(null);
        }
      }

      suspendAudio() {
        if (!this.audible && !this.wantsSound) return;
        this.suspended = true;
        this.setAudible(null);
      }

      resumeAudio() {
        if (!this.suspended) return;
        this.suspended = false;
        this.scheduleReconcile();
      }

      // ---- Quick Add ---------------------------------------------------

      getPanelState(buy) {
        let state = this.panelState.get(buy);
        if (state) return state;
        let variants = [];
        try {
          variants = JSON.parse(buy.querySelector('[data-story-variants]')?.textContent || '[]');
        } catch (error) {
          variants = [];
        }
        const groups = Array.from(buy.querySelectorAll('[data-story-option-group]'));
        state = { variants, groups, selected: groups.map(() => null), choices: 0 };
        // A product option with a single value has nothing to choose:
        // preselect it (never any other option — no auto-picking a size).
        groups.forEach((group) => {
          if (group.querySelectorAll('[data-story-option]').length > 1) state.choices += 1;
        });
        this.resetSelection(buy, state);
        this.panelState.set(buy, state);
        return state;
      }

      // Back to "nothing picked yet" (single-value options stay preselected).
      resetSelection(buy, state = this.getPanelState(buy)) {
        state.groups.forEach((group, index) => {
          const buttons = group.querySelectorAll('[data-story-option]');
          state.selected[index] = buttons.length === 1 ? buttons[0].dataset.value : null;
        });
      }

      openPanel(buy) {
        const panel = buy?.querySelector('[data-story-panel]');
        if (!panel) return;

        // One overlay open at a time.
        this.querySelectorAll('[data-story-panel]:not([hidden])').forEach((other) => {
          const otherBuy = other.closest('[data-story-buy]');
          if (otherBuy !== buy) this.closePanel(otherBuy, false);
        });

        this.hideError(buy);
        this.refreshPanel(buy);
        panel.hidden = false;
        buy.querySelector('[data-story-toggle]')?.setAttribute('aria-expanded', 'true');
        const first = panel.querySelector('[data-story-option]:not(:disabled)') || panel.querySelector('[data-story-close]');
        first?.focus({ preventScroll: true });
      }

      closePanel(buy, refocus) {
        const panel = buy?.querySelector('[data-story-panel]');
        if (!panel || panel.hidden) return;
        panel.hidden = true;
        this.hideError(buy);
        this.resetSelection(buy);
        const toggle = buy.querySelector('[data-story-toggle]');
        toggle?.setAttribute('aria-expanded', 'false');
        if (refocus) toggle?.focus({ preventScroll: true });
      }

      onOptionClick(button) {
        const buy = button.closest('[data-story-buy]');
        const state = this.getPanelState(buy);
        const { variants, selected } = state;
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.value;

        if (state.choices > 1 && selected[index] === value) {
          // In a multi-step choice, tapping the picked value again un-picks it.
          selected[index] = null;
        } else {
          selected[index] = value;
          // Picking a value that is sold out together with another current
          // selection (e.g. a size that does not exist in the chosen colour)
          // must not dead-end the shopper: keep the pick, drop whichever
          // other selections cannot coexist with it in any in-stock variant.
          selected.forEach((chosen, i) => {
            if (i === index || chosen == null) return;
            const coexists = variants.some(
              (variant) => variant.available && variant.options[index] === value && variant.options[i] === chosen
            );
            if (!coexists) selected[i] = null;
          });
        }
        this.hideError(buy);
        this.refreshPanel(buy);

        // Every decision made and it points to exactly one in-stock variant:
        // add it now. No confirm button, no third click.
        if (selected.every((chosen) => chosen != null)) {
          const variant = variants.find((candidate) => candidate.options.every((v, i) => v === selected[i]));
          if (variant && variant.available) {
            this.addVariant(buy, variant.id, button, () => this.refreshPanel(buy));
          }
        }
      }

      // Recomputes which option values can still be bought given the other
      // selections.
      // A value with no in-stock variant at all is truly disabled; one that
      // is only sold out in combination with the current picks is marked
      // unavailable (struck through, aria-disabled) but stays tappable so
      // the shopper can move to it (see onOptionClick).
      refreshPanel(buy) {
        const state = this.getPanelState(buy);
        const { variants, groups, selected } = state;

        const matchesOthers = (variant, index) =>
          selected.every((chosen, i) => i === index || chosen == null || variant.options[i] === chosen);

        groups.forEach((group, index) => {
          group.querySelectorAll('[data-story-option]').forEach((button) => {
            const value = button.dataset.value;
            const inStockAnywhere = variants.some((variant) => variant.available && variant.options[index] === value);
            const inStockWithPicks = variants.some(
              (variant) => variant.available && variant.options[index] === value && matchesOthers(variant, index)
            );
            // Real disabled whenever nothing in stock matches the other picks
            // (e.g. a size sold out in the chosen colour). Tapping a picked
            // value again un-picks it, so this never dead-ends the shopper.
            button.disabled = !(inStockAnywhere && inStockWithPicks);
            delete button.dataset.unavailable;
            button.removeAttribute('aria-disabled');
            button.setAttribute('aria-pressed', selected[index] === value ? 'true' : 'false');
          });
        });
      }

      restoreCta(buy) {
        const cta = buy.querySelector('[data-story-add]');
        const label = cta?.querySelector('[data-story-cta-label]');
        if (label && cta.dataset.defaultLabel) label.textContent = cta.dataset.defaultLabel;
      }

      showError(buy, message) {
        const panel = buy.querySelector('[data-story-panel]');
        const error =
          panel && !panel.hidden ? panel.querySelector('[data-story-panel-error]') : buy.querySelector('[data-story-error]');
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
      }

      hideError(buy) {
        buy.querySelectorAll('[data-story-error], [data-story-panel-error]').forEach((error) => {
          error.hidden = true;
        });
      }

      async addVariant(buy, variantId, button, restore) {
        if (!buy || !variantId || buy.dataset.busy === 'true') return;

        const labelEl = button.querySelector('[data-story-cta-label]');
        const originalLabel = labelEl?.textContent;
        if (button.matches('[data-story-add]') && labelEl && !button.dataset.defaultLabel) {
          button.dataset.defaultLabel = originalLabel;
        }

        buy.dataset.busy = 'true';
        this.hideError(buy);
        buy.querySelector('[data-story-panel]')?.setAttribute('aria-busy', 'true');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (labelEl) labelEl.textContent = buy.dataset.addingLabel;

        let added = false;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || buy.dataset.errorLabel);
          added = true;
        } catch (error) {
          this.showError(buy, error.message || buy.dataset.errorLabel);
        }

        button.removeAttribute('aria-busy');
        buy.querySelector('[data-story-panel]')?.removeAttribute('aria-busy');
        buy.dataset.busy = 'false';

        if (!added) {
          button.disabled = false;
          if (labelEl && originalLabel != null) labelEl.textContent = originalLabel;
          restore?.();
          return;
        }

        if (labelEl) labelEl.textContent = buy.dataset.addedLabel;
        const status = buy.querySelector('[data-story-status]');
        if (status) status.textContent = buy.dataset.addedStatus;

        this.closePanel(buy, false); // also clears the picks
        this.refreshCartCount();
        // Keep the rail silent behind the Cart Drawer this event opens.
        this.suspendAudio();
        document.dispatchEvent(new CustomEvent('pdp:added-to-cart', { detail: { variantId, quantity: 1 } }));

        const timer = setTimeout(() => {
          this.feedbackTimers.delete(timer);
          button.disabled = false;
          restore?.();
        }, ADDED_FEEDBACK_MS);
        this.feedbackTimers.add(timer);
      }

      async refreshCartCount() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const badge = document.querySelector('.site-header__cart-count');
          const cartLink = document.querySelector('.site-header__cart-link');
          if (badge) {
            badge.textContent = cart.item_count;
          } else if (cartLink && cart.item_count > 0) {
            const span = document.createElement('span');
            span.className = 'site-header__cart-count';
            span.setAttribute('aria-hidden', 'true');
            span.textContent = cart.item_count;
            cartLink.appendChild(span);
          }
        } catch (error) {
          /* the count badge is a nicety; the cart drawer refetches the cart itself */
        }
      }
    }
  );
})();

(function initProductQuickShop() {
  if (customElements.get('product-quick-shop')) return;

  const CENTS_TO_UNIT = 100;

  // Mirrors assets/predictive-search.js's formatMoney — same output shape
  // as the theme's server-rendered `money_without_trailing_zeros` ("$ 1,999"),
  // duplicated rather than shared since it's three lines and the two
  // callers have no other code in common.
  function formatMoney(cents) {
    const amount = Math.round(cents) / CENTS_TO_UNIT;
    return `$ ${amount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  customElements.define(
    'product-quick-shop',
    class ProductQuickShop extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.mediaMain = this.querySelector('[data-quick-shop-media-main]');
        this.mediaThumbs = this.querySelector('[data-quick-shop-media-thumbs]');
        this.infoEl = this.querySelector('[data-quick-shop-info]');
        this.prevButton = this.querySelector('[data-quick-shop-prev]');
        this.nextButton = this.querySelector('[data-quick-shop-next]');
        this.closeButtons = this.querySelectorAll('[data-quick-shop-close]');

        this.stories = this.readStoryData();
        this.i18n = this.readI18n();
        this.productCache = new Map();
        this.currentIndex = null;
        this.currentProduct = null;
        this.selectedOptions = [];
        this.requestToken = 0;

        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.pauseMedia();
          this.lastTrigger?.focus();
        });
        this.prevButton?.addEventListener('click', () => this.goToStoryRelative(-1));
        this.nextButton?.addEventListener('click', () => this.goToStoryRelative(1));

        // Delegated: the info/add-to-cart form is re-rendered per product,
        // so its buttons can't have listeners attached individually.
        this.addEventListener('click', (event) => {
          const optionButton = event.target.closest('[data-quick-shop-option]');
          if (optionButton) this.onOptionClick(optionButton);

          const thumb = event.target.closest('[data-quick-shop-thumb]');
          if (thumb) this.setMainMedia(thumb.dataset.mediaIndex);
        });
        this.addEventListener('submit', (event) => {
          if (event.target.matches('[data-quick-shop-form]')) {
            event.preventDefault();
            this.addToCart(event.target);
          }
        });
      }

      readStoryData() {
        const script = this.querySelector('[data-story-data]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      readI18n() {
        const script = this.querySelector('[data-quick-shop-i18n]');
        const fallback = {
          loading: 'Loading…',
          error: 'Something went wrong.',
          quantity: 'Quantity',
          add_to_cart: 'Add to cart',
          sold_out: 'Sold out',
          view_full_product: 'View full product',
          view_media: 'View image',
          added: 'Added to cart.',
        };
        if (!script) return fallback;
        try {
          return Object.assign(fallback, JSON.parse(script.textContent));
        } catch (error) {
          return fallback;
        }
      }

      open(index, triggerEl) {
        if (!this.stories[index]) return;
        this.lastTrigger = triggerEl;
        if (!this.dialog.open) this.dialog.showModal();
        this.goToStory(index);
      }

      close() {
        this.dialog.close();
      }

      // Blocks without a product (see snippets/product-story-card.liquid —
      // they never get a trigger, so open() can't land on one directly)
      // still occupy a slot in this.stories so indices stay aligned with
      // each card's data-story-index. Prev/next has to skip over those
      // slots rather than try to fetch a null handle.
      findStoryIndex(from, direction) {
        for (let i = from; i >= 0 && i < this.stories.length; i += direction) {
          if (this.stories[i]) return i;
        }
        return -1;
      }

      goToStoryRelative(direction) {
        const index = this.findStoryIndex(this.currentIndex + direction, direction);
        if (index !== -1) this.goToStory(index);
      }

      async goToStory(index) {
        if (index < 0 || index >= this.stories.length || !this.stories[index]) return;
        this.currentIndex = index;
        this.prevButton?.toggleAttribute('disabled', this.findStoryIndex(index - 1, -1) === -1);
        this.nextButton?.toggleAttribute('disabled', this.findStoryIndex(index + 1, 1) === -1);

        const story = this.stories[index];
        this.renderLoading();

        const token = ++this.requestToken;
        try {
          const product = await this.getProduct(story.handle);
          if (token !== this.requestToken) return; // a newer story was requested meanwhile
          this.currentProduct = product;
          this.selectedOptions = (product.variants.find((v) => v.available) || product.variants[0]).options.slice();
          this.renderProduct(product, story);
        } catch (error) {
          if (token !== this.requestToken) return;
          this.renderError();
        }
      }

      async getProduct(handle) {
        if (this.productCache.has(handle)) return this.productCache.get(handle);
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
        const data = await response.json();
        this.productCache.set(handle, data);
        return data;
      }

      renderLoading() {
        if (this.infoEl) this.infoEl.innerHTML = `<p class="quick-shop__state text-small text-secondary">${escapeHtml(this.i18n.loading)}</p>`;
        if (this.mediaMain) this.mediaMain.innerHTML = '<div class="media-placeholder quick-shop__media-placeholder"></div>';
        if (this.mediaThumbs) this.mediaThumbs.innerHTML = '';
      }

      renderError() {
        if (this.infoEl) {
          this.infoEl.innerHTML = `<p class="quick-shop__state text-small text-secondary">${escapeHtml(this.i18n.error)}</p>`;
        }
      }

      findVariant(options) {
        return this.currentProduct.variants.find((variant) =>
          variant.options.every((value, i) => value === options[i])
        );
      }

      // For a given option index/value, is there ANY variant that matches
      // it plus the currently-selected values at every other index, and is
      // that variant purchasable? Drives disabling combinations that don't
      // exist or are sold out — never a manual/guessed rule.
      isOptionValueAvailable(optionIndex, value) {
        return this.currentProduct.variants.some(
          (variant) =>
            variant.available &&
            variant.options[optionIndex] === value &&
            variant.options.every((v, i) => i === optionIndex || v === this.selectedOptions[i])
        );
      }

      onOptionClick(button) {
        if (button.disabled) return;
        const index = Number(button.dataset.optionIndex);
        this.selectedOptions[index] = button.dataset.optionValue;
        this.renderProduct(this.currentProduct, this.stories[this.currentIndex]);
      }

      setMainMedia(mediaIndex) {
        const media = this.currentProduct.media[Number(mediaIndex)];
        if (!media || !this.mediaMain) return;
        this.mediaMain.innerHTML = this.mediaHtml(media);
        this.mediaThumbs?.querySelectorAll('[data-quick-shop-thumb]').forEach((thumb) => {
          thumb.classList.toggle('quick-shop__thumb--active', thumb.dataset.mediaIndex === String(mediaIndex));
        });
      }

      mediaHtml(media) {
        if (media.media_type === 'video' || media.media_type === 'external_video') {
          const mp4 = (media.sources || []).find((s) => s.mime_type === 'video/mp4');
          if (mp4) {
            return `<video class="quick-shop__media-video" src="${escapeHtml(mp4.url)}" muted loop playsinline controls preload="none" poster="${escapeHtml(media.preview_image?.src || '')}"></video>`;
          }
        }
        const src = media.preview_image?.src || media.src;
        return `<img class="quick-shop__media-image" src="${escapeHtml(src)}" alt="${escapeHtml(media.alt || '')}" loading="lazy">`;
      }

      renderProduct(product, story) {
        const variant = this.findVariant(this.selectedOptions);
        const media = product.media && product.media.length > 0 ? product.media : null;
        const activeMediaIndex = media ? Math.max(0, media.findIndex((m) => m.id === variant?.featured_media?.id)) : 0;

        if (this.mediaMain) {
          this.mediaMain.innerHTML = media ? this.mediaHtml(media[activeMediaIndex]) : '<div class="media-placeholder quick-shop__media-placeholder"></div>';
        }
        if (this.mediaThumbs) {
          this.mediaThumbs.innerHTML =
            media && media.length > 1
              ? media
                  .map(
                    (m, i) => `
                <button type="button" class="quick-shop__thumb${i === activeMediaIndex ? ' quick-shop__thumb--active' : ''}" data-quick-shop-thumb data-media-index="${i}" aria-label="${escapeHtml(this.i18n.view_media)} ${i + 1}">
                  <img src="${escapeHtml(m.preview_image?.src || '')}&width=120" alt="" loading="lazy">
                </button>`
                  )
                  .join('')
              : '';
        }

        const priceHtml = this.priceHtml(variant);
        const optionsHtml = product.options
          .map((option, optionIndex) => {
            const valuesHtml = option.values
              .map((value) => {
                const available = this.isOptionValueAvailable(optionIndex, value);
                const active = this.selectedOptions[optionIndex] === value;
                return `<button type="button" class="quick-shop__option${active ? ' quick-shop__option--active' : ''}" data-quick-shop-option data-option-index="${optionIndex}" data-option-value="${escapeHtml(value)}" ${available ? '' : 'disabled aria-disabled="true"'}>${escapeHtml(value)}</button>`;
              })
              .join('');
            return `
              <div class="quick-shop__option-group">
                <p class="quick-shop__option-label text-small">${escapeHtml(option.name)}</p>
                <div class="quick-shop__option-values">${valuesHtml}</div>
              </div>`;
          })
          .join('');

        const available = variant ? variant.available : false;

        if (this.infoEl) {
          this.infoEl.innerHTML = `
            ${product.vendor ? `<p class="quick-shop__vendor text-micro">${escapeHtml(product.vendor)}</p>` : ''}
            <h2 class="quick-shop__title">${escapeHtml(product.title)}</h2>
            ${priceHtml}
            <form data-quick-shop-form>
              <input type="hidden" name="id" value="${variant ? variant.id : ''}">
              ${optionsHtml}
              <div class="quick-shop__quantity">
                <label class="text-small" for="QuickShopQty">${escapeHtml(this.i18n.quantity)}</label>
                <input type="number" id="QuickShopQty" name="quantity" min="1" value="1" inputmode="numeric">
              </div>
              <button type="submit" class="button button--primary quick-shop__add" ${available ? '' : 'disabled'}>
                ${available ? escapeHtml(this.i18n.add_to_cart) : escapeHtml(this.i18n.sold_out)}
              </button>
              <p class="quick-shop__add-message text-small" data-quick-shop-message role="status"></p>
            </form>
            <a href="${escapeHtml(product.url)}" class="quick-shop__view-full link-arrow text-small">${escapeHtml(this.i18n.view_full_product)}</a>
          `;
        }
      }

      priceHtml(variant) {
        if (!variant) return '';
        const onSale = variant.compare_at_price > variant.price;
        return `
          <div class="price${onSale ? ' price--on-sale' : ''}">
            ${
              onSale
                ? `<span class="price__sale">${formatMoney(variant.price)}</span><span class="price__compare-at">${formatMoney(variant.compare_at_price)}</span>`
                : `<span class="price__regular">${formatMoney(variant.price)}</span>`
            }
          </div>`;
      }

      async addToCart(form) {
        const button = form.querySelector('.quick-shop__add');
        const message = form.querySelector('[data-quick-shop-message]');
        const id = form.elements.id.value;
        const quantity = Number(form.elements.quantity.value) || 1;
        if (!id) return;

        button.disabled = true;
        button.classList.add('button--loading');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Add to cart failed');

          if (message) message.textContent = this.i18n.added;
          this.updateCartCount();
          document.dispatchEvent(new CustomEvent('pdp:added-to-cart', { detail: { variantId: id, quantity } }));
        } catch (error) {
          if (message) message.textContent = error.message;
        } finally {
          button.disabled = false;
          button.classList.remove('button--loading');
        }
      }

      async updateCartCount() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const badge = document.querySelector('.site-header__cart-count');
          const cartLink = document.querySelector('.site-header__cart-link');
          if (badge) {
            badge.textContent = cart.item_count;
          } else if (cartLink && cart.item_count > 0) {
            const span = document.createElement('span');
            span.className = 'site-header__cart-count';
            span.setAttribute('aria-hidden', 'true');
            span.textContent = cart.item_count;
            cartLink.appendChild(span);
          }
        } catch (error) {
          /* cart count badge is a nicety, not critical to the add itself */
        }
      }

      pauseMedia() {
        this.querySelectorAll('video').forEach((video) => video.pause());
      }
    }
  );
})();
